import { uniqBy, get } from "lodash";
import * as React from "react";
import { convertToDashed } from "@lululemon/mwa-i18n";

/**
 * Parses "sku-style-order" array to build an Object of product image URLs and color swatch image URLs.
 * @param {Array} products - "sku-style-order" Array of Objects where each Object contains data for a SKU
 * @param {String} [size=product_tile] - Used to generate Scene7 preset appended to the URL
 * @returns {Object}
 */

const base = "https://images.lululemon.com/is/image/lululemon/";

// Maximum number of recently viewed products to display
const RECENTLY_VIEWED_COUNT = 8;
// Expiration time for recently viewed products (14 days)
const RECENTLY_VIEWED_EXPIRATION = 14 * 24 * 60 * 60 * 1000;
// Minimum number of recently viewed products to display
const RECENTLY_VIEWED_THRESHOLD = 2;

export const constructImageUrls = (
  skuStyleOrder,
  defaultColor,
  overrideImage
) =>
  skuStyleOrder.map((skuInfo) => {
    if (skuInfo["style-id"]) {
      return overrideImage &&
        defaultColor &&
        skuInfo["color-id"] === defaultColor
        ? {
            src: overrideImage,
            hover: `${base}${skuInfo["style-id"].split("-").join("_")}_2`,
          }
        : {
            src: `${base}${skuInfo["style-id"].split("-").join("_")}_1`,
            hover: `${base}${skuInfo["style-id"].split("-").join("_")}_2`,
          };
    }
    return { src: skuInfo.imageUrl, hover: skuInfo.imageUrl };
  });

export const constructSwatches = (skuStyleOrder) =>
  skuStyleOrder
    .filter((skuInfo) => !!skuInfo["style-id"])
    .map((skuInfoWithStyleId) => ({
      name: skuInfoWithStyleId["color-name"],
      url: `${base}${skuInfoWithStyleId["color-id"]}`,
      id: skuInfoWithStyleId["color-id"],
    }));

/**
 * Given an Array of colours, counts the number of colours and appends "colour(s)" for use as a label.
 * @param {Array} colorGroupArray
 * @returns {String}
 */

export function colorGroupLength(colorGroupArray) {
  if (!colorGroupArray || colorGroupArray.length === 1) {
    return "1 colour";
  }
  return `${colorGroupArray.length} colours`;
}

/**
 * Parses "sku-style-order" array to remove SKU objects that share the same 'style-id' as another SKU in the array.
 * @param {Array} skuStyleOrder - "sku-style-order" Array of Objects where each Object contains data for a SKU
 * @param {String} skuStyleOrder['style-id']
 * @param {String} skuStyleOrder['style-id-02']
 * @returns {Array} Transformed Array of Objects with duplicates on 'style-id' property removed
 */

export function uniqueSkuStyleOrder(skuStyleOrder) {
  // create a duplicate-free version of the array based on 'style-id' property
  const uniqSkuStyleOrder = uniqBy(skuStyleOrder, (item) =>
    item["style-id"].trim()
  );
  // remove trailing white space from 'style-id-02' property (aka the color ID)
  uniqSkuStyleOrder.forEach(
    (item) => (item["style-id-02"] = item["style-id-02"].trim())
  );
  return uniqSkuStyleOrder;
}

/**
 * Transforms a string to replace an asterisk with a line break tag. Generally used on product name.
 * @param {String} text
 * @returns {String}
 */

export function insertLineBreak(text = "") {
  return text.split("*").join("<br>");
}

export function insertLineBreakNoHTML(text = "") {
  return text.split("*").join("\n");
}

export function formatLineBreaks(str, delimiter) {
  const params = str.split(delimiter) ?? [];
  if (!params.length) {
    return str;
  }

  return params.map((param, i) => (
    <>
      {param}
      {i !== params.length - 1 && <br />}
    </>
  ));
}

export const isSelfCareProductTile = (product) =>
  get(product, "parent-category-unified-id", false) === "selfcare";

export const getFormattedPriceOrPriceRange = (
  priceStrOrArr,
  isSalePrice = false
) => {
  let priceOrPriceRange = "";
  const isListPriceAnArr = Array.isArray(priceStrOrArr);
  const isListPriceAStr = typeof priceStrOrArr === "string";

  if (isListPriceAnArr) {
    const [priceLow = "", priceHigh = ""] = priceStrOrArr;

    if (priceHigh && priceLow && !isSalePrice) {
      priceOrPriceRange = `$${priceLow} - $${priceHigh}`;
    } else if (priceHigh || priceLow) {
      priceOrPriceRange = `$${priceHigh || priceLow}`;
    }
  } else if (isListPriceAStr) {
    priceOrPriceRange = `$${priceStrOrArr}`;
  }

  return priceOrPriceRange;
};

export const stripDecimalFromPrice = (price) => {
  if (Array.isArray(price)) return price.map(stripDecimalFromPrice);
  if (typeof price !== "string" && typeof price !== "number") return price;
  const parsedPrice = parseInt(price, 10);
  return Number.isNaN(parsedPrice) ? price : String(parsedPrice);
};

/**
 * Processes product data and sets it in local storage for recently viewed products.
 * @param {Object} productData - The product data to add to the recently viewed list
 * @param {String} color - The color of the product
 * @param {String} locale - The current locale
 * @returns {Object} An object containing the recently viewed products
 */
export function setRecentlyViewedProductData(productData, color, locale) {
  if (typeof window === "undefined" || !productData || !locale) return;

  const dashedLocale = convertToDashed(locale);

  try {
    const {
      "product-summary": {
        "list-price": listPrice,
        "display-name": displayName,
        "product-sale-price": productSalePrice = [],
        "product-id": productId,
        "product-repository-id": productRepositoryId,
        "sku-style-order": skuStyleOrder,
        "currency-code": currencyCode,
      } = {},
      "product-carousel": productCarousel,
    } = productData;
    const colorCode =
      color || productData?.selectedItem?.carousel?.["color-code"];
    // Use current color code to identify product image.
    const srcImage = productCarousel?.find(
      ({ "color-code": itemColorCode }) => itemColorCode === colorCode
    )?.["image-info"]?.[0];
    const id = productId || productRepositoryId;
    const selectedSwatchIndex = 0;
    const skuStyle = skuStyleOrder?.[selectedSwatchIndex];
    const pdpUrl = globalThis.location.pathname + globalThis.location.search;

    const recentlyViewedData = {
      displayName,
      listPrice,
      productSalePrice,
      pdpUrl,
      productId: id,
      imageUrl: srcImage,
      skuStyle,
      id,
      currencyCode,
      locale: dashedLocale,
    };

    if (id && srcImage) {
      setRecentlyViewed(recentlyViewedData, dashedLocale);
      return recentlyViewedData;
    }
  } catch (err) {
    console.trace(err);
  }
}

export function setReviewsDataForLastViewedProduct(reviewsData) {
  if (typeof window === "undefined") return;

  localStorage.setItem("reviewsLastViewed", JSON.stringify(reviewsData));
}

export function getReviewsDataForLastViewedProduct() {
  if (typeof window === "undefined") return {};

  return JSON.parse(localStorage.getItem("reviewsLastViewed"));
}

/**
 * Reads the recently viewed products from local storage, filters out expired and excluded items and returns them.
 * To be used internally. Exported for testing.
 * @param {String} excludeProductId - The ID of the product to exclude from the recently viewed products
 * @param {String} locale - The current locale
 * @returns {Array} An array of recently viewed products with expiration times for a given locale
 */
const getRecentlyViewedLSData = (excludeProductId, locale) => {
  if (typeof window === "undefined" || !locale) return [];

  const dashedLocale = convertToDashed(locale);

  try {
    const recentlyViewedData = JSON.parse(
      localStorage.getItem("recentlyViewedData") || "{}"
    );

    const recentlyViewedForLocale = recentlyViewedData[dashedLocale] || [];

    return recentlyViewedForLocale
      .filter((item) => item?.expirationTime > Date.now())
      .filter((item) => item.product.id !== excludeProductId)
      .slice(0, RECENTLY_VIEWED_COUNT);
  } catch (e) {
    return [];
  }
};

/**
 * Sets the recently viewed product in local storage.
 * @param {Object} newProduct - The product to add to the recently viewed list
 * @param {String} locale - The locale to store the product under
 * @returns {Object} An object containing the recently viewed products
 */
export const setRecentlyViewed = (newProduct, locale) => {
  if (typeof window === "undefined" || !newProduct || !locale || !localStorage)
    return { recentlyViewedLSData: {} };

  const dashedLocale = convertToDashed(locale);

  const currentTime = Date.now();
  const newItem = {
    product: { ...newProduct },
    expirationTime: currentTime + RECENTLY_VIEWED_EXPIRATION,
  };

  let recentlyViewedData;
  try {
    const data = localStorage.getItem("recentlyViewedData");
    recentlyViewedData = data ? JSON.parse(data) : {};
  } catch (error) {
    recentlyViewedData = {};
  }

  const currentRecentlyViewedForLocale = getRecentlyViewedLSData(
    newProduct.id,
    dashedLocale
  );

  currentRecentlyViewedForLocale.unshift(newItem);

  recentlyViewedData[dashedLocale] = currentRecentlyViewedForLocale;

  try {
    localStorage.setItem(
      "recentlyViewedData",
      JSON.stringify(recentlyViewedData)
    );
  } catch (error) {
    console.error("Error setting recently viewed product:", error);
  }
  return { recentlyViewedLSData: recentlyViewedData };
};

/**
 * Checks if there are at least 2 recently viewed products not including the current product.
 * @param {String} currentProductId - The ID of the current product (to exclude from the recently viewed products)
 * @param {String} locale - The current locale
 * @returns {Boolean} Whether there are at least 2 recently viewed products
 */
export const shouldShowRecentlyViewed = (currentProductId, locale) => {
  if (!locale) {
    return false;
  }

  return (
    getRecentlyViewedProducts(currentProductId, locale).length >=
    RECENTLY_VIEWED_THRESHOLD
  );
};

/**
 * Returns the recently viewed products from local storage, filtered out expired and excluded items.
 * @param {String} currentProductId - The ID of the current product (to exclude from the recently viewed products)
 * @param {String} locale - The current locale
 * @returns {Array} An array of recently viewed products for a given locale
 */
export const getRecentlyViewedProducts = (currentProductId, locale) => {
  if (!locale) return [];

  const dashedLocale = convertToDashed(locale);

  let recentlyViewedProducts = getRecentlyViewedLSData(
    currentProductId,
    dashedLocale
  ).map((item) => item.product);

  if (recentlyViewedProducts.length < RECENTLY_VIEWED_THRESHOLD) {
    recentlyViewedProducts = [];
  }

  return recentlyViewedProducts;
};

/**
 * Constructs product colors, images, and swatches based on the provided product SKU style order.
 * @param {Array} productSkuStyleOrder - Array containing SKU style order for the product.
 * @returns {Object} - An object containing colors, images, and swatches constructed from the provided SKU style order.
 */

export const buildProductColors = (productSkuStyleOrder = []) => {
  const skuStyleOrder = uniqueSkuStyleOrder(productSkuStyleOrder);
  const images = constructImageUrls(skuStyleOrder);
  const swatches = constructSwatches(skuStyleOrder);
  const colors = swatches.reduce((swatchColors, swatchData, i) => {
    const imageData = images?.[parseInt(i)];

    if (!!imageData && imageData.src.includes(swatchData.id)) {
      const { src: imageUrl, hover: hoverImageUrl } = imageData;
      swatchColors.push({ swatch: swatchData, imageUrl, hoverImageUrl });
    }

    return swatchColors;
  }, []);

  return {
    colors,
    images,
    swatches,
  };
};
